import React, { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { getTests } from '../../../apiClients/backendApi';
import useAsync from '../../../hooks/useAsync';
import { useTitle } from '../../../systems/TitleManager';
import { Add } from '@mui/icons-material';
import { startCase } from 'lodash';
import ChangeStatusAction from './ChangeStatusAction';
import DataGridDyl from '../../../components/DataGridDyl';
import { LevelText } from '@dyl/systems/LevelsManager';
import ScreenActions from '@dyl/components/ScreenActions';
import { Fab, Grid, MenuItem, TextField } from '@mui/material';
import LinkButton from '@dyl/components/LinkButton';
import quickDateFormat from '@dyl/utils/quickDateFormat';
import deleteTestGroup from "@dyl/apiClients/backendApi/deleteTestGroup";
import GenericDeleteAction from "@dyl/components/GenericDeleteAction";
import TestStatuses from "@dyl/constants/TestStatuses";
import useChangeEvent from "@dyl/hooks/useChangeEvent";
import SearchField from "@dyl/components/SearchField";
import withLevel from "@dyl/utils/withLevel";

export default withLevel(TestList);
const DEFAULT_STATUS = 'show_all_except_archived'

function TestList({ globalLevel }) {
    useTitle('Tests');
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [triggerReload, setTriggerReload] = useState(0);

    const tests = useAsync(async () => {
        setLoading(true);

        try {
            return await getTests(globalLevel.level_id);
        }
        catch {
            enqueueSnackbar('Failed to load tests', { variant: 'error' });
        }
        finally {
            setLoading(false);
        }
    }, [triggerReload, globalLevel]) ?? [];

    const [status, setStatus] = useState(DEFAULT_STATUS);
    const onStatusChange = useChangeEvent(setStatus);

    const [search, setSearch] = useState('');
    const onSearch = useChangeEvent(setSearch);

    const filteredTests = useMemo(() => {
        const caseInsensitiveSearch = search.toLowerCase();

        return tests.filter(test =>
            test?.name?.toLowerCase()?.includes(caseInsensitiveSearch) &&
            (status === DEFAULT_STATUS ? test.status !== 'archived' : test.status === status)
        );
    }, [tests, search, status]);

    const columns = useMemo(() => {
        /** @type {import('@mui/x-data-grid').GridColDef[]} */
        const columns = [
            { headerName: 'Test ID', field: 'name', flex: 3 },
            { headerName: 'Level', field: 'level_id', flex: 2, renderCell({ value }) {
                    return <LevelText levelId={value} />;
                }
                },
            { headerName: 'Last modified', field: 'updated_at', flex: 2, valueFormatter({ value }) {
                    return quickDateFormat(value);
                }
                },
            { headerName: 'Status', field: 'status', flex: 2, valueFormatter({ value }) {
                    return startCase(value)
                }
                },
            { headerName: 'Status Change', field: 'status_change', flex: 2, renderCell({ row }) {
                    return (
                        <ChangeStatusAction
                            test={row}
                            levelId={row.level_id ?? globalLevel?.level_id}
                        />
                    );
                }
                },
            { headerName: 'Delete', field: 'ADMIN_DELETE', flex: 2, renderCell({ row }) {
                    return (<GenericDeleteAction
                        disabled={row.status !== 'draft'}
                        data={row}
                        promptTexts={
                            {
                                dialogTitle: () => 'Delete Test?',
                                dialogText: (data) => `Are you sure you want to delete test "${data.name}"?`,
                                ongoingNotification: (data) => `Deleting test "${data.name}"...`,
                                successNotification: (data, response) => `Deleted test "${data.name}".`,
                                failureNotification: (data, response) => `Was unable to delete test "${data.name}" for an unknown reason.`,
                                errorNotification: (data) => `Failed to delete test "${data.name}".`
                            }
                        }
                        deleteAction={(data) => deleteTestGroup(data.test_group_id)}
                        responseSuccessPredicate={(response) => response?.success}
                        onDelete={() => setTriggerReload(triggerReload + 1)}
                    />)
                }
            },
        ];

        return columns;
    }, [globalLevel]);

    const getRowUrl = useCallback(({ row: test }) => {
        return `/${globalLevel.label}/tests/${test.test_group_id}`;
    }, []);

    return (
        <>
            <FilterBlock status={status} onStatusChange={onStatusChange} search={search} onSearch={onSearch} />

            <DataGridDyl
                rows={filteredTests}
                columns={columns}
                getRowId={getRowId}
                getRowUrl={getRowUrl}
                disableColumnFilter
                variant='links'
                loading={loading}
            />

            <ScreenActions>
                <LinkButton
                    component={Fab}
                    color='primary'
                    variant='extended'
                    size='large'
                    to={`/${globalLevel.label}/tests/new`}
                >
                    <Add />&nbsp;New
                </LinkButton>
            </ScreenActions>
        </>
    );
}

function FilterBlock({ search, onSearch, status, onStatusChange }) {
    return(
        <Grid container alignItems='center' spacing={4}>
            <Grid item md={4}>
                <SearchField
                    fullWidth
                    value={search}
                    onChange={onSearch}
                />
            </Grid>
            <Grid item md={3}>
                <TextField
                    label='Status'
                    select
                    fullWidth
                    value={status}
                    onChange={onStatusChange}
                    defaultValue={DEFAULT_STATUS}
                >
                    <MenuItem value={DEFAULT_STATUS}>
                        Show all except archived
                    </MenuItem>
                    {TestStatuses.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                            {label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
        </Grid>);
}

const getRowId = test => test?.test_group_id;